import type { Graphics } from 'pixi.js'
import {
  boardLocalLengthToAmbient,
  boardLocalToAmbient,
  boardPositionCenter,
  getBoggleBoardSurfaceBridgeState,
  setBoggleAmbientBoardEffects,
  type BoggleAmbientBoardEffect,
  type BoggleBoardSurfaceSnapshot,
} from './boardSurfaceBridge'
import { drawTileShapePath } from '~/boggle/ui/run/pixi/tileShapeDraw'

/** Expanding yellow ring on tile press (ambient layer). */
export const TILE_PRESS_PULSE_MS = 800
const TILE_PRESS_PULSE_START_SCALE = 1
const TILE_PRESS_PULSE_END_SCALE = 2.35
const TILE_PRESS_PULSE_STROKE_WIDTH = 3
const TILE_PRESS_PULSE_MAX_ALPHA = 0.4
const TILE_PRESS_PULSE_COLOR = 0xe2b96a

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function easeOutCubic(progress: number) {
  return 1 - (1 - progress) ** 3
}

export function tilePressPulseVisual(elapsedMs: number): { alpha: number; scale: number } {
  const progress = clamp(elapsedMs / TILE_PRESS_PULSE_MS, 0, 1)
  const eased = easeOutCubic(progress)
  return {
    alpha: (1 - eased) * TILE_PRESS_PULSE_MAX_ALPHA,
    scale:
      TILE_PRESS_PULSE_START_SCALE +
      eased * (TILE_PRESS_PULSE_END_SCALE - TILE_PRESS_PULSE_START_SCALE),
  }
}

export function pruneExpiredBoggleAmbientBoardEffects(wallClockMs: number) {
  const { effects } = getBoggleBoardSurfaceBridgeState()
  setBoggleAmbientBoardEffects(
    effects.filter(effect => {
      if (effect.type === 'tilePressPulse') {
        return wallClockMs - effect.startedAtMs < TILE_PRESS_PULSE_MS
      }

      return false
    }),
  )
}

export function boggleAmbientBoardEffectsNeedTicker(
  effects: readonly BoggleAmbientBoardEffect[],
  wallClockMs: number,
): boolean {
  return effects.some(effect => {
    if (effect.type !== 'tilePressPulse') {
      return false
    }

    const elapsed = wallClockMs - effect.startedAtMs
    return elapsed < TILE_PRESS_PULSE_MS && tilePressPulseVisual(elapsed).alpha > 0.002
  })
}

export function drawBoggleAmbientBoardEffects(
  graphics: Graphics,
  snapshot: BoggleBoardSurfaceSnapshot,
  ambientHostRect: DOMRectReadOnly,
  ambientCanvasW: number,
  ambientCanvasH: number,
  effects: readonly BoggleAmbientBoardEffect[],
  wallClockMs: number,
) {
  graphics.clear()

  for (const effect of effects) {
    if (effect.type !== 'tilePressPulse') {
      continue
    }

    const elapsedMs = wallClockMs - effect.startedAtMs
    const { alpha, scale } = tilePressPulseVisual(elapsedMs)
    if (alpha <= 0.002) {
      continue
    }

    const boardCenter = boardPositionCenter(snapshot.layout, effect.position)
    const ambientCenter = boardLocalToAmbient(
      snapshot,
      ambientHostRect,
      ambientCanvasW,
      ambientCanvasH,
      boardCenter.x,
      boardCenter.y,
    )
    const baseRadius = snapshot.layout.tileSize / 2
    const ambientRadius = boardLocalLengthToAmbient(
      snapshot,
      ambientHostRect,
      ambientCanvasW,
      ambientCanvasH,
      baseRadius * scale,
    )

    drawTileShapePath(
      graphics,
      effect.tileShape,
      ambientCenter.x,
      ambientCenter.y,
      ambientRadius,
    )
    graphics.stroke({
      alpha,
      color: TILE_PRESS_PULSE_COLOR,
      width: TILE_PRESS_PULSE_STROKE_WIDTH,
    })
  }
}
